/**
 * Drafts View - Shows draft messages
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import {
  makeStyles,
  Button,
  Text,
  Spinner,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
} from '@fluentui/react-components';
import {
  DraftsRegular,
  EditRegular,
  DeleteRegular,
  MoreHorizontalRegular,
  AttachRegular,
} from '@fluentui/react-icons';
import { ApiClient, newToast } from '../../../../configurations/index.config';
import { getErrorMessage } from '../../../../types';
import { formatRelativeDate, stripHtmlTags } from '../../../../utils/memoUtils';
import { TablePagination } from '../../../../components/common';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  header: {
    marginBottom: '16px',
  },
  title: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '4px',
  },
  messageList: {
    flex: 1,
    overflowY: 'auto',
    border: '1px solid #e1dfdd',
    borderRadius: '4px',
  },
  messageItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    borderBottom: '1px solid #e1dfdd',
    cursor: 'pointer',
    transition: 'background-color 0.1s ease',
    ':hover': {
      backgroundColor: '#f9f9f9',
    },
  },
  messageIcon: {
    marginRight: '12px',
    fontSize: '20px',
    color: '#605e5c',
  },
  messageContent: {
    flex: 1,
    minWidth: 0,
  },
  subject: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '2px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  snippet: {
    fontSize: '13px',
    color: '#605e5c',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  messageTime: {
    fontSize: '13px',
    color: '#605e5c',
    marginLeft: '12px',
    whiteSpace: 'nowrap',
  },
  actionButton: {
    minWidth: 'auto',
    marginLeft: '12px',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 20px',
    color: '#605e5c',
    minHeight: '300px',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
  },
});

interface Draft {
  message_id: number;
  subject: string;
  snippet: string;
  has_attachments: boolean;
  attachment_count: number;
  updated_at: string;
  created_at: string;
}

interface ContextType {
  stats: any;
  mailbox: any;
  refreshStats: () => void;
}

export const DraftsFluentUI: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { refreshStats } = useOutletContext<ContextType>();
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const totalPages = Math.ceil(totalItems / pageSize);

  useEffect(() => {
    loadDrafts();
  }, [currentPage, pageSize]);

  const loadDrafts = async () => {
    try {
      setLoading(true);
      const response = await ApiClient.get('/memo/drafts', {
        params: {
          page: currentPage,
          limit: pageSize,
        },
      });

      if (response.data.status === 'success') {
        setDrafts(response.data.data.items || []);
        setTotalItems(response.data.data.pagination?.total || 0);
      }
    } catch (error) {
      newToast('error', getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleEditDraft = (draftId: number) => {
    navigate(`/account/memo/compose?draft=${draftId}`);
  };

  const handleDeleteDraft = async (draftId: number) => {
    if (!window.confirm('Are you sure you want to delete this draft?')) {
      return;
    }

    try {
      const response = await ApiClient.delete(`/memo/draft/${draftId}`);

      if (response.data.status === 'success') {
        newToast('success', 'Draft deleted');
        loadDrafts();
        refreshStats();
      }
    } catch (error) {
      newToast('error', getErrorMessage(error));
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="large" label="Loading drafts..." />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Text className={styles.title}>Drafts</Text>
        <Text size={300} style={{ color: '#605e5c' }}>
          {totalItems} draft{totalItems !== 1 ? 's' : ''}
        </Text>
      </div>

      <div className={styles.messageList}>
        {drafts.length === 0 ? (
          <div className={styles.emptyState}>
            <DraftsRegular style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }} />
            <Text size={400}>No drafts</Text>
          </div>
        ) : (
          drafts.map(draft => (
            <div
              key={draft.message_id}
              className={styles.messageItem}
              onClick={() => handleEditDraft(draft.message_id)}
            >
              <div className={styles.messageIcon}>
                <DraftsRegular />
              </div>

              <div className={styles.messageContent}>
                <Text className={styles.subject}>{draft.subject || '(No Subject)'}</Text>
                <Text className={styles.snippet}>{stripHtmlTags(draft.snippet)}</Text>
              </div>

              {draft.has_attachments && (
                <AttachRegular style={{ fontSize: '16px', color: '#605e5c', marginLeft: '12px' }} />
              )}

              <Text className={styles.messageTime}>{formatRelativeDate(draft.updated_at)}</Text>

              <Menu>
                <MenuTrigger>
                  <Button
                    appearance="subtle"
                    icon={<MoreHorizontalRegular />}
                    className={styles.actionButton}
                    onClick={e => e.stopPropagation()}
                  />
                </MenuTrigger>
                <MenuPopover>
                  <MenuList>
                    <MenuItem
                      icon={<EditRegular />}
                      onClick={() => handleEditDraft(draft.message_id)}
                    >
                      Edit draft
                    </MenuItem>
                    <MenuItem
                      icon={<DeleteRegular />}
                      onClick={() => handleDeleteDraft(draft.message_id)}
                    >
                      Delete draft
                    </MenuItem>
                  </MenuList>
                </MenuPopover>
              </Menu>
            </div>
          ))
        )}
      </div>

      {totalPages > 1 && (
        <div style={{ marginTop: '16px' }}>
          <TablePagination
            totalItems={totalItems}
            currentPage={currentPage}
            itemsPerPage={pageSize}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={() => {}}
          />
        </div>
      )}
    </div>
  );
};

export default DraftsFluentUI;
